import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  handleUpdateFieldsConfig,
  getPaymentMethodCashierFields,
  CashierField,
  ALLOW_ZERO_AMOUNT_CONFIG_KEY,
  applyAllowZeroAmountConfig,
  CLIENT_METHOD_TYPE_CONFIG_KEY,
  RESERVED_PAYMENT_CONFIG_KEYS,
} from "store/payments/methods/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import ActionBtn from "components/buttons/ActionBtn";

import FilterTabs from "components/FilterTabs";
import { Modal } from "components/Modal/Modal";
import binIcon from "assets/icons/bin-icon.svg";
import plusIcon from "assets/icons/sidebar/plus.svg";
import { handleApiError } from "helpers/handleApiError";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import Checkbox from "components/inputs/Checkbox/Checkbox";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import PaymentMethodConfigScopeTabs from "./PaymentMethodConfigScopeTabs";
import MethodsAutoWithdrawalTab from "./MethodsAutoWithdrawalTab";
import { resolveClientMethodTypeStatusLabel } from "./clientMethodTypeChip";

interface PaymentMethodsConfigTabProps {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  setFieldError: (field: string, code: string) => void;
  isFetched: boolean;
  isReadOnly: boolean;
  showAutoWithdrawal: boolean;
}

export const RESERVED_CUSTOM_CONFIG_ERROR_FIELD = "customConfig.reservedField";

export const hasReservedCustomConfigError = (errors: Record<string, string>) =>
  Boolean(errors[RESERVED_CUSTOM_CONFIG_ERROR_FIELD]);

const MethodsConfigTab: React.FC<PaymentMethodsConfigTabProps> = ({
  fieldErrors,
  clearFieldError,
  setFieldError,
  isFetched,
  isReadOnly,
  showAutoWithdrawal,
}) => {
  const canEdit = !isReadOnly;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const payloadConfig = useAppSelector(
    (store) => store["payment-methods"].config,
  );
  const cashierFields = useAppSelector(
    (store) => store["payment-methods"].cashierFields,
  );
  const currenciesList = useAppSelector((store) => store.settings.currencies);

  const [customConfigFields, setCustomConfigFields] = useState<
    { key: string; value: string }[]
  >([]);
  const customConfigHydrationScopeRef = useRef<string | null>(null);
  const [activeSection, setActiveSection] = useState<
    "params" | "fields" | "auto"
  >("params");
  const sectionTabs = showAutoWithdrawal
    ? (["params", "fields", "auto"] as const)
    : (["params", "fields"] as const);
  useEffect(() => {
    if (!isFetched) {
      customConfigHydrationScopeRef.current = null;
      return;
    }
    const hydrationScope = `${payloadConfig?.country}|${payloadConfig?.currency}`;
    if (customConfigHydrationScopeRef.current === hydrationScope) {
      return;
    }
    const result: { key: string; value: string }[] = [];
    if (payloadConfig?.config) {
      Object.keys(payloadConfig?.config).forEach((el) => {
        if (
          el !== ALLOW_ZERO_AMOUNT_CONFIG_KEY &&
          !RESERVED_PAYMENT_CONFIG_KEYS.includes(
            el as (typeof RESERVED_PAYMENT_CONFIG_KEYS)[number],
          )
        ) {
          result.push({
            key: el,
            value: String(payloadConfig.config[el] ?? ""),
          });
        }
      });
    }
    setCustomConfigFields(result);
    customConfigHydrationScopeRef.current = hydrationScope;
  }, [
    isFetched,
    payloadConfig?.config,
    payloadConfig?.country,
    payloadConfig?.currency,
  ]);

  useEffect(() => {
    const loadCashierFields = async () => {
      try {
        await dispatch(getPaymentMethodCashierFields()).unwrap();
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors: undefined,
        });
      }
    };
    loadCashierFields();
  }, [dispatch, t]);

  const buildConfigWithCustomFields = (
    fields: { key: string; value: string }[],
  ) => {
    const reservedConfig = Object.fromEntries(
      Object.entries(payloadConfig?.config ?? {}).filter(([key]) =>
        RESERVED_PAYMENT_CONFIG_KEYS.includes(
          key as (typeof RESERVED_PAYMENT_CONFIG_KEYS)[number],
        ),
      ),
    );
    const editableConfig = Object.fromEntries(
      fields
        .filter(
          ({ key }) =>
            key !== "" &&
            !RESERVED_PAYMENT_CONFIG_KEYS.includes(
              key as (typeof RESERVED_PAYMENT_CONFIG_KEYS)[number],
            ),
        )
        .map(({ key, value }) => [key, value]),
    );
    return applyAllowZeroAmountConfig({
      ...editableConfig,
      ...reservedConfig,
    });
  };

  const addCustomField = () => {
    setCustomConfigFields([...customConfigFields, { key: "", value: "" }]);
  };

  const syncReservedCustomConfigError = (
    fields: { key: string; value: string }[],
  ) => {
    if (fields.some(({ key }) => key === ALLOW_ZERO_AMOUNT_CONFIG_KEY)) {
      setFieldError(
        RESERVED_CUSTOM_CONFIG_ERROR_FIELD,
        "ReservedPaymentConfigField",
      );
      return;
    }
    clearFieldError(RESERVED_CUSTOM_CONFIG_ERROR_FIELD);
  };

  const handleCustomFieldChange = (
    index: number,
    field: "key" | "value",
    value: string,
  ) => {
    const updatedFields = customConfigFields.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: value } : item,
    );

    setCustomConfigFields(updatedFields);
    syncReservedCustomConfigError(updatedFields);

    const configKey = updatedFields[index].key;

    if (configKey !== "") {
      dispatch(
        // @ts-ignore
        handleUpdateFieldsConfig({
          config: buildConfigWithCustomFields(updatedFields),
        }),
      );
    }
  };

  const clientMethodType =
    typeof payloadConfig?.config?.[CLIENT_METHOD_TYPE_CONFIG_KEY] === "string"
      ? payloadConfig.config[CLIENT_METHOD_TYPE_CONFIG_KEY]
      : "";
  const clientMethodTypeRef = useRef(clientMethodType);
  clientMethodTypeRef.current = clientMethodType;
  const clientMethodTypeStatusLabel =
    resolveClientMethodTypeStatusLabel(clientMethodType);

  const allowZeroAmount =
    payloadConfig?.config?.[ALLOW_ZERO_AMOUNT_CONFIG_KEY] === true;

  const updateAllowZeroAmount = (checked: boolean) => {
    const nextConfig = applyAllowZeroAmountConfig({
      ...(payloadConfig?.config ?? {}),
    });
    if (checked) {
      nextConfig[ALLOW_ZERO_AMOUNT_CONFIG_KEY] = true;
    } else {
      delete nextConfig[ALLOW_ZERO_AMOUNT_CONFIG_KEY];
    }
    dispatch(
      // @ts-ignore
      handleUpdateFieldsConfig({
        config: nextConfig,
      }),
    );
  };

  const updateClientMethodType = (value: string) => {
    clientMethodTypeRef.current = value;
    const nextConfig = { ...(payloadConfig?.config ?? {}) };
    if (value.trim() === "") {
      delete nextConfig[CLIENT_METHOD_TYPE_CONFIG_KEY];
    } else {
      nextConfig[CLIENT_METHOD_TYPE_CONFIG_KEY] = value;
    }
    dispatch(
      // @ts-ignore
      handleUpdateFieldsConfig({
        config: nextConfig,
      }),
    );
  };

  const handleData = (key: string, value: string | boolean | number | null) => {
    // @ts-ignore
    dispatch(handleUpdateFieldsConfig({ [key]: value }));
  };

  const removeCustomField = (index: number) => {
    const updatedFields = [...customConfigFields];
    updatedFields.splice(index, 1)[0];
    setCustomConfigFields(updatedFields);
    syncReservedCustomConfigError(updatedFields);
    dispatch(
      // @ts-ignore
      handleUpdateFieldsConfig({
        config: buildConfigWithCustomFields(updatedFields),
      }),
    );
  };

  const handleCashierFieldToggle = (field: CashierField, checked: boolean) => {
    const currentFields = payloadConfig?.cashierFields ?? [];

    const updatedFields = checked
      ? [...currentFields, field]
      : currentFields.filter((item) => item.code !== field.code);

    dispatch(
      // @ts-ignore
      handleUpdateFieldsConfig({
        cashierFields: updatedFields,
      }),
    );
  };

const depositCountOptions = Array.from({ length: 11 }, (_, index) => ({
  value: index,
  label: String(index),
}));

  return (
    <div className="payment-config-form">
      <PaymentMethodConfigScopeTabs />

      {payloadConfig?.country && payloadConfig?.currency && (
        <>
          {!isFetched ? (
            <ModalTabSkeleton />
          ) : (
            <>
              <div className="bonus-form-content">
                <div className="bonus-form-box">
                  <div className="bonus-form-box--item">
                    <InnerInput
                      value={payloadConfig?.minAmount ?? ""}
                      placeholder={t("minAmount")}
                      label={t("minAmount")}
                      type="number"
                      id="payment-config-minAmount"
                      onChange={(e) => {
                        handleData("minAmount", e);
                        clearFieldError("minAmount");
                      }}
                      hasError={Boolean(fieldErrors?.["minAmount"])}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
                <div className="bonus-form-box">
                  <div className="bonus-form-box--item">
                    <InnerInput
                      value={payloadConfig?.maxAmount ?? ""}
                      placeholder={t("maxAmount")}
                      label={t("maxAmount")}
                      type="number"
                      id="payment-config-maxAmount"
                      onChange={(e) => {
                        handleData("maxAmount", e);
                        clearFieldError("maxAmount");
                      }}
                      hasError={Boolean(fieldErrors?.["maxAmount"])}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
                <div className="bonus-form-box">
                  <div className="bonus-form-box--item">
                    <DefaultDropdown
                      value={payloadConfig?.fromCompletedDepositCount ?? 0}
                      placeholder={t("minCompletedDeposits")}
                      label={t("minCompletedDeposits")}
                      tooltipText={t("minCompletedDepositsTooltip")}
                      tooltipPosition="top-left"
                      options={depositCountOptions}
                      disabled={!canEdit}
                      onSelect={(e) => {
                        handleData("fromCompletedDepositCount", Number(e));
                        clearFieldError("fromCompletedDepositCount");
                      }}
                    />
                  </div>
                </div>
                <div className="bonus-form-box">
                  <div className="bonus-form-box--item">
                    <DefaultDropdown
                      value={payloadConfig?.toCompletedDepositCount ?? 0}
                      placeholder={t("maxCompletedDeposits")}
                      label={t("maxCompletedDeposits")}
                      tooltipText={t("maxCompletedDepositsTooltip")}
                      tooltipPosition="top-left"
                      options={depositCountOptions}
                      disabled={!canEdit}
                      onSelect={(e) => {
                        handleData("toCompletedDepositCount", Number(e));
                        clearFieldError("toCompletedDepositCount");
                      }}
                      hasError={Boolean(
                        fieldErrors?.["toCompletedDepositCount"],
                      )}
                    />
                  </div>
                </div>
                <fieldset className="payment-config-display-labels payment-config-allow-zero-amount">
                  <legend>{t("amountOptions")}</legend>
                  <div className="payment-config-display-options">
                    <Checkbox
                      id="payment-config-allowZeroAmount"
                      txt={t("allowZeroAmount")}
                      value={allowZeroAmount}
                      onCheck={(checked) => {
                        updateAllowZeroAmount(checked);
                        clearFieldError(`config.${ALLOW_ZERO_AMOUNT_CONFIG_KEY}`);
                      }}
                      hasError={Boolean(
                        fieldErrors?.[`config.${ALLOW_ZERO_AMOUNT_CONFIG_KEY}`],
                      )}
                      disabled={!canEdit}
                    />
                  </div>
                </fieldset>
              </div>
              <div className="bonus-form-custom-row payment-config-type-row">
                <div className="bonus-form-box--item">
                  <FilterTabs
                    value={
                      clientMethodTypeStatusLabel ? undefined : payloadConfig?.type
                    }
                    statusLabel={clientMethodTypeStatusLabel}
                    isInner
                    label={t("type")}
                    options={[
                      {
                        value: "card",
                        label: t("card"),
                      },
                      {
                        value: "eWallet",
                        label: t("eWallet"),
                      },
                      {
                        value: "bankTransfer",
                        label: t("bankTransfer"),
                      },
                      {
                        value: "crypto",
                        label: t("crypto"),
                      },
                      {
                        value: "mobilePayment",
                        label: t("mobilePayment"),
                      },
                      {
                        value: "voucher",
                        label: t("voucher"),
                      },
                      {
                        value: "cash",
                        label: t("cash"),
                      },
                    ]}
                    onSelect={(e) => {
                      handleData("type", e);
                      clearFieldError("type");
                    }}
                    hasError={Boolean(fieldErrors?.["type"])}
                    disabled={!canEdit}
                  />
                </div>
                <div className="payment-config-type-controls">
                  <div className="bonus-form-box--item payment-config-client-method-type">
                  <InnerInput
                    value={clientMethodType}
                    placeholder={t("clientMethodTypePlaceholder")}
                    label={t("clientMethodType")}
                    tooltipText={t("clientMethodTypeTooltip")}
                    tooltipPosition="top-left"
                    type="text"
                    id="payment-config-clientMethodType"
                    onChange={(value) => {
                      updateClientMethodType(value);
                      clearFieldError(`config.${CLIENT_METHOD_TYPE_CONFIG_KEY}`);
                    }}
                    onBlur={() => {
                      const trimmed = clientMethodTypeRef.current.trim();
                      if (trimmed !== clientMethodTypeRef.current) {
                        updateClientMethodType(trimmed);
                      }
                    }}
                    hasError={Boolean(
                      fieldErrors?.[`config.${CLIENT_METHOD_TYPE_CONFIG_KEY}`],
                    )}
                    disabled={!canEdit}
                  />
                  </div>
                  <div className="bonus-form-box--item payment-config-active-slot">
                  <SwitchBtn
                    activeTxt={t("active")}
                    isActive={!!payloadConfig?.isActive}
                    handleClick={() => {
                      handleData("isActive", !!payloadConfig?.isActive ? 0 : 1);
                      clearFieldError("isActive");
                    }}
                    hasError={Boolean(fieldErrors?.["isActive"])}
                    disabled={!canEdit}
                  />
                  </div>
                </div>
              </div>

              <div className="payment-config-display-row">
                <div>
                  <InnerInput
                    value={String(payloadConfig?.rewardPercent ?? 0)}
                    label={t("rewardPercent")}
                    tooltipText={t("rewardPercentTooltip")}
                    tooltipPosition="top-left"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    id="payment-config-rewardPercent"
                    onChange={(value) => {
                      handleData("rewardPercent", value);
                      clearFieldError("rewardPercent");
                    }}
                    onBlur={() => {
                      if (payloadConfig?.rewardPercent === "") {
                        handleData("rewardPercent", 0);
                      }
                    }}
                    hasError={Boolean(fieldErrors?.rewardPercent)}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <DefaultDropdown
                    value={payloadConfig?.rewardCalculationLimitTargetCurrency ?? ""}
                    placeholder={t("current")}
                    label={t("rewardCalculationLimitTargetCurrency")}
                    tooltipText={t("rewardCalculationLimitTargetCurrencyTooltip")}
                    tooltipPosition="top-left"
                    options={[
                      { label: t("current"), value: "" },
                      ...currenciesList.map((code) => ({ label: code, value: code })),
                    ]}
                    disabled={!canEdit}
                    onSelect={(value) => {
                      handleData("rewardCalculationLimitTargetCurrency", value || null);
                      clearFieldError("rewardCalculationLimitTargetCurrency");
                    }}
                  />
                </div>
                <div>
                  <InnerInput
                    value={payloadConfig?.rewardCalculationLimit ?? ""}
                    label={t("rewardCalculationLimit")}
                    tooltipText={t("rewardCalculationLimitTooltip")}
                    tooltipPosition="top-left"
                    type="text"
                    inputMode="decimal"
                    id="payment-config-rewardCalculationLimit"
                    onChange={(value) => {
                      handleData("rewardCalculationLimit", value);
                      clearFieldError("rewardCalculationLimit");
                    }}
                    hasError={Boolean(fieldErrors?.rewardCalculationLimit)}
                    disabled={!canEdit}
                  />
                </div>
                <fieldset className="payment-config-display-labels">
                  <legend>{t("displayLabels")}</legend>
                  <div className="payment-config-display-options">
                    {([
                      ["isVip", "vip"],
                      ["isTop", "top"],
                      ["isPopular", "popular"],
                      ["isNew", "new"],
                    ] as const).map(([field, label]) => (
                      <Checkbox
                        key={field}
                        id={`payment-config-${field}`}
                        txt={t(label)}
                        value={payloadConfig?.[field] ?? false}
                        onCheck={(checked) => {
                          handleData(field, checked);
                          clearFieldError(field);
                        }}
                        hasError={Boolean(fieldErrors?.[field])}
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                </fieldset>
              </div>

              <div className="config-inputs-wrapper">
                <Modal.Tabs
                  className="config-section-tabs"
                  tabs={[...sectionTabs]}
                  activeTab={activeSection}
                  onClick={setActiveSection}
                  isSmallModifier
                />
                {activeSection === "params" && (
                  <>
                    {canEdit && (
                      <ActionBtn
                        txt={t("addParam")}
                        handleClick={addCustomField}
                        mainIcon={plusIcon}
                        isWithText
                      />
                    )}
                    {customConfigFields.length > 0 && (
                      <>
                        <div className="config-inputs--header">
                          <span>{t("Key")}</span>
                          <span>{t("Value")}</span>
                          <span aria-hidden="true" />
                        </div>
                        {customConfigFields.map((field, index) => {
                          const hasReservedKeyConflict =
                            field.key === ALLOW_ZERO_AMOUNT_CONFIG_KEY;
                          return (
                            <div key={index} className="config-inputs--item">
                              <div className="config-inputs--field">
                                <InnerInput
                                  value={field.key}
                                  placeholder={t("Key")}
                                  type="text"
                                  onChange={(e) => {
                                    handleCustomFieldChange(index, "key", e);
                                    clearFieldError?.(`config.${field.key}`);
                                  }}
                                  hasError={
                                    hasReservedKeyConflict ||
                                    Boolean(fieldErrors?.[`config.${field.key}`])
                                  }
                                  id={`custom-config-key-${index}`}
                                  disabled={!canEdit}
                                />
                                {hasReservedKeyConflict && (
                                  <span
                                    className="config-inputs--error"
                                    role="alert"
                                  >
                                    {t("reservedPaymentConfigFieldError")}
                                  </span>
                                )}
                              </div>
                              <InnerInput
                                value={field.value}
                                placeholder={t("Value")}
                                type="text"
                                onChange={(e) => {
                                  handleCustomFieldChange(index, "value", e);
                                  clearFieldError?.(`config.${field.key}`);
                                }}
                                id={`custom-config-value-${index}`}
                                hasError={Boolean(
                                  fieldErrors?.[`config.${field.key}`],
                                )}
                                disabled={!canEdit}
                              />
                              {canEdit && (
                                <div className="delete-btn">
                                  <img
                                    src={binIcon}
                                    alt=""
                                    onClick={() => removeCustomField(index)}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </>
                )}

                {activeSection === "fields" && (
                  <div className="payment-method-fields">
                    {cashierFields.map((field) => (
                      <Checkbox
                        key={field.code}
                        value={
                          payloadConfig?.cashierFields?.some(
                            (item) => item.code === field.code,
                          ) ?? false
                        }
                        txt={field.title}
                        id={field.code}
                        onCheck={(checked) =>
                          handleCashierFieldToggle(field, checked)
                        }
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                )}

                {activeSection === "auto" && showAutoWithdrawal && (
                  <MethodsAutoWithdrawalTab
                    fieldErrors={fieldErrors}
                    clearFieldError={clearFieldError}
                    setFieldError={setFieldError}
                    isReadOnly={isReadOnly}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MethodsConfigTab;
